import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { Incident } from "../../../../types/incident";
import { useTranslation } from "react-i18next";
import { isMediumPriorityLabel, resolvePriorityColor } from "../../../../assets/theme/geminiPalette";
import { formatRelativeFromNow } from "../../../../lib/datetime/displayDateTime";
import {
  hasDetectedAnomalyEvents,
  IncidentPriorityChip,
  IncidentStatusChip,
  resolveDetectedAnomalyTone,
} from "../../lib/incidentChips";

type IncidentListItemProps = {
  incident: Incident;
  selected: boolean;
  onSelect: (incident: Incident) => void;
};

export default function IncidentListItem({ incident, selected, onSelect }: IncidentListItemProps) {
  const { t } = useTranslation();
  const isNew = incident.status === "NEW";
  const hasAnomaly = hasDetectedAnomalyEvents(incident);
  const anomalyTone = resolveDetectedAnomalyTone(incident);
  const customerName = incident.customer?.name?.trim() || "—";
  const category = incident.category?.trim() || "—";
  const subcategory = incident.subcategory?.trim() || "—";
  const classificationLabel = `${category} / ${subcategory}`;
  const fullDescription = (incident.description || "").trim().replace(/\s+/g, " ");
  const snippet =
    fullDescription.length > 20 ? `${fullDescription.slice(0, 20)}...` : fullDescription;

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(incident)}
      data-incident-id={incident.id}
      alignItems="flex-start"
      sx={{
        py: 1.25,
        px: 2,
        borderBottom: 1,
        borderColor: "divider",
        gap: 1.25,
        "&.Mui-selected": {
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          borderLeft: "3px solid",
          borderLeftColor: "primary.main",
          pl: "calc(16px - 3px)",
          "&:hover": {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
          },
        },
      }}
    >
      <Box
        sx={{
          width: 4,
          alignSelf: "stretch",
          borderRadius: 1,
          bgcolor: resolvePriorityColor(incident.priority),
          flexShrink: 0,
          opacity: selected ? 1 : 0.85,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          title={customerName}
          noWrap
          sx={{
            display: "block",
            mb: 0.15,
            fontSize: "0.68rem",
            lineHeight: 1.3,
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          {customerName}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          title={classificationLabel}
          noWrap
          sx={{
            display: "block",
            mb: 0.2,
            fontSize: "0.68rem",
            lineHeight: 1.3,
            letterSpacing: "0.02em",
          }}
        >
          {classificationLabel}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.35 }}>
          <Typography
            variant="body2"
            fontWeight={isNew ? 800 : 600}
            noWrap
            sx={{ flex: 1, minWidth: 0, color: isNew ? "text.primary" : "text.primary" }}
          >
            {incident.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            {formatRelativeFromNow(incident.createdDate)}
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          title={fullDescription || undefined}
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.4,
            mb: 0.75,
          }}
        >
          {snippet || "No description"}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
          <IncidentPriorityChip incident={incident} compact />
          <IncidentStatusChip status={incident.status} compact variant="outlined" />
          {incident.assignedTo ? (
            <Chip
              label={incident.assignedTo}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.62rem" }}
            />
          ) : null}
          {hasAnomaly && anomalyTone ? (
            <Chip
              label={t("common.anomaly")}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.62rem",
                fontWeight: 700,
                bgcolor: anomalyTone.color,
                color:
                  !anomalyTone.severity || isMediumPriorityLabel(anomalyTone.severity)
                    ? "#1f1f1f"
                    : "common.white",
              }}
            />
          ) : null}
        </Stack>
      </Box>
    </ListItemButton>
  );
}
